import { IInventorysFilterOptions } from '@app/interfaces/inventory';
import { tableDataInit } from '@app/pages/FishingOperations/Inspections/constants';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';

export interface InventoryFilterState {
  filterOptions: IInventorysFilterOptions;
  shouldTriggerFetch: boolean;
  selectedWaterbodyIds: number[];
  selectedEmployeeIds: number[];
  selectedTypeIds: number[];
  selectedWaterbodyIdsTemporal: number[];
  selectedEmployeeIdsTemporal: number[];
  selectedTypeIdsTemporal: number[];
  fromPlannedFilter: Dayjs | null | undefined;
  toPlannedFilter: Dayjs | null | undefined;
  fromExecutedFilter: Dayjs | null | undefined;
  toExecutedFilter: Dayjs | null | undefined;
  onlyPlanned: boolean | null;
  onlyExecuted: boolean | null;
}

const initialState: InventoryFilterState = {
  filterOptions: tableDataInit.filterOptions,
  shouldTriggerFetch: true,
  selectedWaterbodyIds: [],
  selectedEmployeeIds: [],
  selectedTypeIds: [],
  selectedWaterbodyIdsTemporal: [],
  selectedEmployeeIdsTemporal: [],
  selectedTypeIdsTemporal: [],
  fromPlannedFilter: undefined,
  toPlannedFilter: undefined,
  fromExecutedFilter: undefined,
  toExecutedFilter: undefined,
  onlyPlanned: null,
  onlyExecuted: null,
};

const inventoryFiltersSlice = createSlice({
  name: 'inventoryFilters',
  initialState,
  reducers: {
    setFilterOptions(state, action: PayloadAction<IInventorysFilterOptions>) {
      state.filterOptions = action.payload;
    },
    setShouldTriggerFetch(state, action: PayloadAction<boolean>) {
      state.shouldTriggerFetch = action.payload;
    },
    setSelectedWaterbodyIds(state, action: PayloadAction<number[]>) {
      state.selectedWaterbodyIds = action.payload;
    },
    setSelectedEmployeeIds(state, action: PayloadAction<number[]>) {
      state.selectedEmployeeIds = action.payload;
    },
    setSelectedTypeIds(state, action: PayloadAction<number[]>) {
      state.selectedTypeIds = action.payload;
    },
    setSelectedWaterbodyIdsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedWaterbodyIdsTemporal = action.payload;
    },
    setSelectedEmployeeIdsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedEmployeeIdsTemporal = action.payload;
    },
    setSelectedTypeIdsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedTypeIdsTemporal = action.payload;
    },
    setFromPlannedFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.fromPlannedFilter = action.payload;
      state.shouldTriggerFetch = true;
    },
    setToPlannedFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.toPlannedFilter = action.payload;
      state.shouldTriggerFetch = true;
    },
    setFromExecutedFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.fromExecutedFilter = action.payload;
      state.shouldTriggerFetch = true;
    },
    setToExecutedFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.toExecutedFilter = action.payload;
      state.shouldTriggerFetch = true;
    },
    setOnlyPlanned(state, action: PayloadAction<boolean | null>) {
      state.onlyPlanned = action.payload;
      state.shouldTriggerFetch = true;
    },
    setOnlyExecuted(state, action: PayloadAction<boolean | null>) {
      state.onlyExecuted = action.payload;
      state.shouldTriggerFetch = true;
    },
    resetFilters(state) {
      state.selectedWaterbodyIds = initialState.selectedWaterbodyIds;
      state.selectedEmployeeIds = initialState.selectedEmployeeIds;
      state.selectedTypeIds = initialState.selectedTypeIds;
      state.selectedWaterbodyIdsTemporal = initialState.selectedWaterbodyIdsTemporal;
      state.selectedEmployeeIdsTemporal = initialState.selectedEmployeeIdsTemporal;
      state.selectedTypeIdsTemporal = initialState.selectedEmployeeIdsTemporal;
      state.fromPlannedFilter = initialState.fromPlannedFilter;
      state.toPlannedFilter = initialState.toPlannedFilter;
      state.fromExecutedFilter = initialState.fromExecutedFilter;
      state.toExecutedFilter = initialState.toExecutedFilter;
      state.onlyPlanned = initialState.onlyPlanned;
      state.onlyExecuted = initialState.onlyExecuted;
      state.shouldTriggerFetch = true;
    },
  },
});

export const {
  setFilterOptions,
  setShouldTriggerFetch,
  setSelectedWaterbodyIds,
  setSelectedEmployeeIds,
  setSelectedTypeIds,
  setSelectedWaterbodyIdsTemporal,
  setSelectedEmployeeIdsTemporal,
  setSelectedTypeIdsTemporal,
  setFromPlannedFilter,
  setToPlannedFilter,
  setFromExecutedFilter,
  setToExecutedFilter,
  setOnlyPlanned,
  setOnlyExecuted,
  resetFilters,
} = inventoryFiltersSlice.actions;

export const areFiltersAppliedSelector = (state: { inventoryFilters: InventoryFilterState }) => {
  const {
    selectedWaterbodyIds,
    selectedEmployeeIds,
    selectedTypeIds,
    fromPlannedFilter,
    toPlannedFilter,
    fromExecutedFilter,
    toExecutedFilter,
    onlyPlanned,
    onlyExecuted,
  } = state.inventoryFilters;

  return (
    !!selectedWaterbodyIds.length ||
    !!selectedEmployeeIds.length ||
    !!selectedTypeIds.length ||
    !!fromPlannedFilter ||
    !!toPlannedFilter ||
    !!fromExecutedFilter ||
    !!toExecutedFilter ||
    !!onlyPlanned ||
    !!onlyExecuted
  );
};

export const activeFiltersCountSelector = (state: { inventoryFilters: InventoryFilterState }) => {
  const {
    selectedWaterbodyIds,
    selectedEmployeeIds,
    selectedTypeIds,
    fromPlannedFilter,
    toPlannedFilter,
    fromExecutedFilter,
    toExecutedFilter,
    onlyPlanned,
    onlyExecuted,
  } = state.inventoryFilters;

  let count = 0;
  if (selectedWaterbodyIds.length > 0) count++;
  if (selectedEmployeeIds.length > 0) count++;
  if (selectedTypeIds.length > 0) count++;
  if (fromPlannedFilter) count++;
  if (toPlannedFilter) count++;
  if (fromExecutedFilter) count++;
  if (toExecutedFilter) count++;
  if (onlyPlanned) count++;
  if (onlyExecuted) count++;

  return count;
};

export const fetchParamsSelector = (state: { inventoryFilters: InventoryFilterState }) => {
  const {
    selectedWaterbodyIds,
    selectedEmployeeIds,
    selectedTypeIds,
    fromPlannedFilter,
    toPlannedFilter,
    fromExecutedFilter,
    toExecutedFilter,
    onlyPlanned,
    onlyExecuted,
  } = state.inventoryFilters;

  return {
    selectedWaterbodyIds,
    selectedEmployeeIds,
    selectedTypeIds,
    fromPlannedFilter,
    toPlannedFilter,
    fromExecutedFilter,
    toExecutedFilter,
    onlyPlanned,
    onlyExecuted,
  };
};

export const inventoryFiltersActions = inventoryFiltersSlice.actions;

export const inventoryFilters = inventoryFiltersSlice.reducer;
